import {
  buttonVariants,
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
} from '@nudool/design-system';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { TagIcon } from 'lucide-react';
import { useMemo } from 'react';
import { ListEmptyState } from '../../components/ListEmptyState.js';
import { ListErrorState } from '../../components/ListErrorState.js';
import {
  parseListRouteSearch,
  type ListRouteSearch,
} from '../../hooks/use-list-table-helpers/index.js';
import { useListTable } from '../../hooks/use-list-table.js';
import { PermissionGuard } from '../../lib/permission-guard.js';
import { makeBrandColumns } from '../../modules/brands/brand-columns.js';
import {
  GET_BRANDS,
  type BrandListItem,
  type BrandWhere,
  type GetBrandsQueryResult,
  type GetBrandsQueryVariables,
} from '../../modules/brands/operations.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';

// Column id -> Prisma orderBy path, verified against SORTABLE_FIELDS.Brand.
export const BRAND_SORT_MAP: Record<string, string[]> = {
  name: ['name'],
  activeFlag: ['activeFlag'],
};

const BRANDS_WHERE: BrandWhere = { activeFlag: { equals: true } };

export const Route = createFileRoute('/brands/')({
  validateSearch: (search: Record<string, unknown>): ListRouteSearch =>
    parseListRouteSearch(search),
  component: BrandsPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function BrandsPage() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('brand:edit');
  const columns = useMemo(() => makeBrandColumns(navigate, canEdit), [navigate, canEdit]);

  const {
    rows: brands,
    loading,
    error,
    refetch,
    tableProps,
    search,
  } = useListTable<GetBrandsQueryResult, GetBrandsQueryVariables, BrandListItem, BrandWhere>({
    query: GET_BRANDS,
    extractConnection: (data) => data?.brands,
    defaultSort: [{ id: 'name', desc: false }],
    sortMap: BRAND_SORT_MAP,
    sortTiebreakerField: null, // BrandOrderBy has no `id` field yet.
    where: BRANDS_WHERE,
  });

  const newBrandAction = (
    <PermissionGuard
      required={['brand:edit']}
      pass={
        <Link to="/brands/new" className={buttonVariants({})}>
          + New Brand
        </Link>
      }
      fail={
        <span className={buttonVariants({})} role="link" aria-disabled="true">
          + New Brand
        </span>
      }
    />
  );

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">Brands</DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Manage the brand labels sales orders, purchase orders, and products are grouped under.
        </DimeCardDescription>
        <DimeCardAction>{newBrandAction}</DimeCardAction>
      </DimeCardHeader>
      {error && brands.length === 0 ? (
        <ListErrorState
          title="Could not load brands"
          message={error.message}
          onRetry={() => void refetch()}
        />
      ) : !loading && brands.length === 0 && !search ? (
        <ListEmptyState
          icon={<TagIcon />}
          title="No brands yet"
          description="Create your first brand to start grouping sales orders, purchase orders, and products."
          action={newBrandAction}
        />
      ) : (
        <DimeDataTable
          columns={columns}
          data={brands}
          maxBodyHeight="viewport"
          searchColumn="name"
          searchPlaceholder="Search by name…"
          showViewOptions
          emptyMessage="No brands match your search."
          onRowClick={(brand) => {
            void navigate({
              to: '/brands/$brandId',
              params: { brandId: brand.id },
              search: { edit: false },
            });
          }}
          loadingRowCount={tableProps.pageSize}
          exportFileName="brands"
          {...tableProps}
        />
      )}
    </DimeCard>
  );
}
